// VerdeMobility Careers — shared job logic
// This is the React version of what js/jobs-render.js used to do.
// Nothing here touches the DOM — these are plain functions that
// components call and use the return value to render JSX.

import { getDepartmentLabel } from "./filterOptions";

const NEW_BADGE_DAYS = 14;
export const HOMEPAGE_JOB_COUNT = 3;
export const RELATED_JOB_COUNT = 3;

// Active jobs only, sorted by sortOrder (ascending).
export function getActiveJobs(jobs) {
  return jobs
    .filter((job) => job.status === "active")
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

// True if a job was posted within the last NEW_BADGE_DAYS days.
export function isNewJob(job) {
  if (!job.postedDate) return false;
  const posted = new Date(job.postedDate + "T00:00:00");
  const diffDays = (Date.now() - posted.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= NEW_BADGE_DAYS;
}

// Builds a mailto: link with recipient + subject pre-filled, so
// "Apply Now" opens the candidate's email app ready to send.
export function buildMailto(job) {
  const subject = encodeURIComponent(job.applicationSubject || `${job.title} Application`);
  return `mailto:${job.applyEmail}?subject=${subject}`;
}

// Narrows a list of jobs by keyword (title + teaser + department label),
// department (exact match on the short value, "" = no filter), and
// location (exact match on the short value, "" = no filter).
//
// `filters.department` / `filters.location` are the short VALUES from
// filterOptions.js (e.g. "operations"), not the display labels — that's
// what the <select> in 02_searchBar.jsx now sends, with "" meaning
// "All Departments" / "All Locations".
export function filterJobs(jobs, filters) {
  if (!filters) return jobs;
  const keyword = (filters.keyword || "").trim().toLowerCase();
  const department = filters.department || "";
  const location = filters.location || "";

  return jobs.filter((job) => {
    const matchesKeyword =
      !keyword ||
      `${job.title} ${job.teaser || ""} ${getDepartmentLabel(job.department)}`.toLowerCase().includes(keyword);
    const matchesDept = !department || job.department === department;
    const matchesLocation = !location || job.location === location;
    return matchesKeyword && matchesDept && matchesLocation;
  });
}

// NOTE: department/location dropdown options are now managed manually in
// static/filterOptions.js (DEPARTMENTS / LOCATIONS) instead of being
// auto-built from jobsData.js. getFilterOptions() below is kept in case
// you ever want to go back to the auto-generated approach, but it's not
// currently used anywhere.
export function getFilterOptions(activeJobs) {
  const departments = [...new Set(activeJobs.map((j) => j.department))];
  const locations = [...new Set(activeJobs.map((j) => j.location))];
  return { departments, locations };
}

// These two sections aren't written by hand in jobsData.js — they're
// built automatically from fields every job already has.
export function autoSections(job) {
  const sections = [];

  if (job.experience) {
    sections.push({ heading: "Experience", type: "paragraph", body: job.experience });
  }

  if (job.applicationSubject || job.applicationNote) {
    const parts = [];
    if (job.applicationSubject) {
      parts.push(`Candidates must send their application with subject "${job.applicationSubject}".`);
    }
    if (job.applicationNote) parts.push(job.applicationNote);
    sections.push({ heading: "Application Guidelines", type: "paragraph", body: parts.join(" ") });
  }

  return sections;
}

// Finds a job by its slug (used by the Career Details page route param).
export function findJobBySlug(jobs, slug) {
  return jobs.find((j) => j.slug === slug);
}

// Same-department jobs, excluding this one, active only.
export function getRelatedJobs(jobs, job, count = RELATED_JOB_COUNT) {
  return getActiveJobs(jobs)
    .filter((j) => j.department === job.department && j.slug !== job.slug)
    .slice(0, count);
}